import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import type { SvgIconComponent } from '@material-ui/icons';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import SupervisorAccount from '@material-ui/icons/SupervisorAccount';
import UpdateIcon from '@material-ui/icons/Update';
import { makeStyles } from '@material-ui/styles';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import parseISO from 'date-fns/parseISO';
import keyBy from 'lodash/fp/keyBy';
import mapValues from 'lodash/fp/mapValues';
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';

import { Alert } from 'src/common/ui/components';
import ConfirmDialog from 'src/components/pages/pageElements/confirmDialog';
import { useClinicActions } from 'src/dropInClinic/ClinicActions.hook';
import { requestTypeMap } from 'src/dropInClinic/RequestType';
import RequiresSchedulingWarning from 'src/dropInClinic/RequiresSchedulingWarning';
import { CAN_PATIENT_BE_SCHEDULED } from 'src/dropInClinic/queries/canPatientBeScheduled.gql';
import { GET_PATIENT_DROP_IN_HOURS } from 'src/dropInClinic/queries/getPatientDropInHours.gql';
import { CLINIC_ACTIONS_OVERRIDE } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import {
  CanPatientBeScheduledForClinicQuery,
  DropInClinicPatientHoursQuery,
} from 'src/generated/gql/graphql';
import Colors from 'src/nightingale/Colors';
import { PageTitle } from 'src/nightingale/components/common/PageTitle/PageTitle';
import { roundToNearestMinutes } from 'src/util/roundToNearestMinutes';

type RequestType = keyof typeof requestTypeMap;

const iconMap: Record<
  Extract<
    RequestType,
    'FOLLOW_UP' | 'INTAKE' | 'PEER_VISIT' | 'REENGAGEMENT' | 'CASE_MANAGER_VISIT'
  >,
  SvgIconComponent
> = {
  FOLLOW_UP: UpdateIcon,
  INTAKE: GroupAddIcon,
  PEER_VISIT: SupervisedUserCircleIcon,
  REENGAGEMENT: RotateRightIcon,
  CASE_MANAGER_VISIT: SupervisorAccount,
};

const HoursDisplay = ({ hoursData }) => {
  if (!hoursData) return <></>;

  return (
    <div
      style={{
        marginTop: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        color: Colors.Gray7,
        fontFamily: '"Nunito", "Nunito Sans"',
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 1.1,
      }}
    >
      Pop-In Clinic hours:
      {Object.entries(hoursData)
        .filter(([, queueHours]) => Array.isArray(queueHours) && queueHours.length)
        .map(([queueType, queueHours]) => (
          <div
            key={queueType}
            style={{
              margin: 8,
            }}
          >
            {queueType}
            {(queueHours as string[]).map(hours => (
              <div key={hours}>
                {'\n \u2022 '}
                {hours}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

export const LegacyClinicActions = ({ patientId }: { patientId: string }) => {
  const classes = useStyles();

  const [exceedingQueue, setExceedingQueue] = useState<RequestType | null>(null);
  const { state, createRequest } = useClinicActions(patientId);
  const isLoading = state?.state === 'loading';
  const loadingRequestType = isLoading ? state?.requestType : null;
  const spinner = <CircularProgress size={20} />;

  const { data: hoursData } = useSWR<DropInClinicPatientHoursQuery>([
    GET_PATIENT_DROP_IN_HOURS,
    { patientId },
  ]);
  const { data: canBeScheduledData } = useSWR<CanPatientBeScheduledForClinicQuery>([
    CAN_PATIENT_BE_SCHEDULED,
    { patientId, requestTypes: Object.keys(requestTypeMap) },
  ]);

  const canBeScheduledMap = useMemo(() => {
    const queryResultMap = keyBy(
      x => x.requestType,
      canBeScheduledData?.staff_canPatientBeScheduledForClinic ?? [],
    );
    return mapValues(
      ({
        canBeScheduled,
        canBeScheduledToday,
        availabilityWindowEnd,
        willUnscheduleAnotherPatient,
      }) => ({
        canBeScheduled,
        canBeScheduledToday,
        willUnscheduleAnotherPatient,
        availabilityWindowEnd: parseISO(availabilityWindowEnd),
      }),
      queryResultMap,
    );
  }, [canBeScheduledData]);

  const canOverrideQueue = useFeatureFlag(CLINIC_ACTIONS_OVERRIDE);

  const hasSchedulingComplication = (requestType: RequestType) =>
    !canBeScheduledMap[requestType]?.canBeScheduled ||
    canBeScheduledMap[requestType].willUnscheduleAnotherPatient;

  const attemptCreateRequestAutopilot = async (requestType: RequestType) => {
    if (hasSchedulingComplication(requestType)) {
      setExceedingQueue(requestType);
    } else {
      await createRequest(requestType);
    }
  };

  const QueueButton = ({ requestType }: { requestType: RequestType }) => {
    const Icon = iconMap[requestType];

    return (
      <Button
        color="secondary"
        variant="contained"
        startIcon={isLoading && loadingRequestType === requestType ? spinner : <Icon />}
        disabled={isLoading || !canOverrideQueue}
        onClick={() => attemptCreateRequestAutopilot(requestType)}
        className={hasSchedulingComplication(requestType) ? 'will-not-be-seen' : 'will-be-seen'}
        style={{
          width: '400px',
          marginRight: '10px',
          backgroundColor: hasSchedulingComplication(requestType) ? Colors.Coral : '',
        }}
      >
        {requestTypeMap[requestType]}
      </Button>
    );
  };

  const ScheduleEstimate = ({ requestType }: { requestType: RequestType }) => {
    const {
      canBeScheduled,
      canBeScheduledToday,
      willUnscheduleAnotherPatient,
      availabilityWindowEnd,
    } = canBeScheduledMap[requestType] || {};

    // This is an intentional loose equality to catch both null and undefined
    if (canBeScheduled == null) {
      return null;
    }

    const minutes = differenceInMinutes(
      availabilityWindowEnd,
      roundToNearestMinutes(new Date(), { roundingMethod: 'ceil' }),
    );

    let scheduleText = 'We estimate that this patient WILL NOT be seen TODAY';
    if (willUnscheduleAnotherPatient)
      scheduleText = `We estimate that this patient will be seen in the next ${minutes}, but this patient WILL UNSCHEDULE another patient`;
    else if (canBeScheduled)
      scheduleText = `We estimate that this patient WILL be seen in the next ${minutes} minutes`;
    else if (canBeScheduledToday)
      scheduleText = `We estimate that this patient WILL NOT be seen in the next ${minutes} minutes`;

    return (
      <div className={hasSchedulingComplication(requestType) ? 'will-not-be-seen' : 'will-be-seen'}>
        {scheduleText}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 950 }}>
      <PageTitle>Clinic Actions</PageTitle>
      <RequiresSchedulingWarning patientId={patientId} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.keys(requestTypeMap).map((requestType: RequestType) => {
          return (
            <div className={classes.requestTypeRow}>
              <QueueButton requestType={requestType} />
              <div className={classes.capacityColumn}>
                <ScheduleEstimate requestType={requestType} />
              </div>
            </div>
          );
        })}
        {state?.state === 'error' && (
          <Alert
            title="Error creating Pop-In Clinic request"
            message={state.errors?.join(', ') || 'Error adding patient to queue.'}
            severity="error"
          />
        )}
        {state?.state === 'no_availability' && (
          <Alert
            title="Error creating Pop-In Clinic request"
            message="No availability for patient."
            severity="error"
          />
        )}
        {state?.state === 'success' && (
          <>
            <Alert severity="success" title="Patient added to queue" />
            {state.warning && <Alert severity="info" message={state.warning} />}
          </>
        )}
        <HoursDisplay hoursData={hoursData?.getPatientDropInClinicHours} />
      </div>
      {exceedingQueue && (
        <ConfirmDialog
          isDestructive
          onSubmit={async () => {
            await createRequest(exceedingQueue);
            setExceedingQueue(null);
          }}
          onCancel={() => setExceedingQueue(null)}
          submitLabel="Add patient"
        >
          Are you sure that you would like to exceed available capacity
        </ConfirmDialog>
      )}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  requestTypeRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  capacityColumn: {
    display: 'flex',
    flexDirection: 'column',

    '& .will-not-be-seen': {
      color: Colors.Coral,
    },
  },
}));
